import { useState } from "react";
import { motion } from "framer-motion";

import { useAIChat } from "@/components/ai-assistant/useAIChat";
import SessionsRail from "@/components/ai-assistant/SessionsRail";
import AssistantHeader from "@/components/ai-assistant/AssistantHeader";
import MessageList from "@/components/ai-assistant/MessageList";
import ComposerInput from "@/components/ai-assistant/ComposerInput";

/**
 * AIAssistantDock — slide-in chat panel with streaming + multi-model + history.
 *
 * Internals are split into focused modules under `components/ai-assistant/`:
 *   - useAIChat (hook): state + SSE streaming + session CRUD
 *   - SessionsRail / AssistantHeader / MessageList / ComposerInput (UI parts)
 *
 * This file is now just the orchestration shell — open/close animations and
 * layout. All chat behaviour lives in the hook.
 */
export default function AIAssistantDock({ onClose }) {
  const chat = useAIChat();
  const [showHistory, setShowHistory] = useState(false);

  const handleLoadSession = (sid) => {
    chat.loadSession(sid).then(() => setShowHistory(false));
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[60] flex justify-end"
      style={{ background: "rgba(15,23,42,0.4)", backdropFilter: "blur(6px)" }}
      onClick={onClose}
    >
      <motion.aside
        initial={{ x: 100, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        exit={{ x: 60, opacity: 0 }}
        transition={{ type: "spring", damping: 24 }}
        onClick={(e) => e.stopPropagation()}
        data-testid="ai-assistant-panel"
        className="h-full w-full sm:w-[560px] glass-heavy flex border-l border-slate-200"
      >
        {showHistory && (
          <SessionsRail
            sessions={chat.sessions}
            sessionId={chat.sessionId}
            onNewChat={() => { chat.newChat(); setShowHistory(false); }}
            onLoad={handleLoadSession}
            onDelete={chat.deleteSession}
          />
        )}

        <div className="flex-1 flex flex-col min-w-0">
          <AssistantHeader
            provider={chat.provider}
            onProviderChange={chat.setProvider}
            showHistory={showHistory}
            onToggleHistory={() => setShowHistory((v) => !v)}
            onClose={onClose}
          />
          <MessageList ref={chat.scrollRef} messages={chat.messages} busy={chat.busy} />
          <ComposerInput
            input={chat.input}
            onChange={chat.setInput}
            onSend={chat.send}
            busy={chat.busy}
          />
        </div>
      </motion.aside>
    </motion.div>
  );
}
